// API Configuration for different environments
import { Capacitor } from '@capacitor/core';
import axios, { type InternalAxiosRequestConfig } from 'axios';
import {
  getAuthHeaderForPath,
  getBaristaTokenSync,
  hydrateAuthTokens,
  isAuthHydrated,
  setBaristaProfile,
  setBaristaToken,
} from '../utils/authStorage';

/** Production API — used when REACT_APP_API_URL is unset (e.g. Android APK must not default to a random LAN IP). */
const DEFAULT_PUBLIC_API_URL = 'https://api.qahwahub.com';

/** Older env values used the Render hostname, including a one-letter typo. */
const LEGACY_API_HOSTS = ['qahwahub-api.onrender.com', 'qawhahub-api.onrender.com'];

const normalizeApiUrl = (raw: string) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  return LEGACY_API_HOSTS.reduce(
    (url, host) => url.replace(host, 'api.qahwahub.com'),
    trimmed
  );
};

const getApiUrl = () => {
  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    const apiUrl = normalizeApiUrl(process.env.REACT_APP_API_URL || DEFAULT_PUBLIC_API_URL);
    console.log('📱 Mobile API URL:', apiUrl);
    return apiUrl;
  }

  const configuredWebUrl = normalizeApiUrl(process.env.REACT_APP_API_URL || '');
  if (configuredWebUrl) {
    console.log('🌐 Web API URL:', configuredWebUrl);
    return configuredWebUrl;
  }

  // Web dev: empty string = same-origin / CRA dev proxy (`/api` → localhost:5000)
  // Web prod: default to public API host so Vercel deployments don't depend on rewrites.
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalDevHost = host === 'localhost' || host === '127.0.0.1';
  const webUrl = isLocalDevHost ? '' : DEFAULT_PUBLIC_API_URL;
  console.log('🌐 Web API URL:', webUrl || '/api (proxy)');
  return webUrl;
};

export const API_BASE_URL = getApiUrl();

axios.defaults.baseURL = API_BASE_URL;

// Render free tier can cold-start ~30–60s; 10s caused false "network" failures
axios.defaults.timeout = 60000;

void hydrateAuthTokens();

function readBearerFromHeaders(headers: InternalAxiosRequestConfig['headers']): string | null {
  if (!headers) return null;
  const raw =
    typeof (headers as { get?: (name: string) => unknown }).get === 'function'
      ? (headers as { get: (name: string) => unknown }).get('Authorization')
      : (headers as { Authorization?: unknown; authorization?: unknown }).Authorization ||
        (headers as { authorization?: unknown }).authorization;
  if (typeof raw !== 'string') return null;
  const match = raw.match(/^Bearer\s+(.+)$/i);
  return match?.[1] || null;
}

function setAuthorizationHeader(config: InternalAxiosRequestConfig, token: string) {
  const value = `Bearer ${token}`;
  const headers = config.headers as { set?: (name: string, value: string) => void } | undefined;
  if (headers && typeof headers.set === 'function') {
    headers.set('Authorization', value);
    return;
  }
  config.headers = config.headers || {};
  (config.headers as { Authorization: string }).Authorization = value;
}

function attachAuthToConfig(config: InternalAxiosRequestConfig) {
  const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
  const auth = getAuthHeaderForPath(path) || readBearerFromHeaders(config.headers);
  if (auth) {
    setAuthorizationHeader(config, auth);
  }
  return config;
}

// Staff console (/barista) uses barista JWT; consumer app uses customer/guest token.
// Keep this interceptor sync after the first hydrate so Axios actually sends Authorization.
axios.interceptors.request.use(
  (config) => {
    if (isAuthHydrated()) {
      return attachAuthToConfig(config);
    }
    return hydrateAuthTokens().then(() => attachAuthToConfig(config));
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Render free tier: first request after sleep may return 503 (hibernate-wake-error)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as
      | (InternalAxiosRequestConfig & { __retryWake?: boolean; __skipWakeRetry?: boolean })
      | undefined;
    const status = error.response?.status;
    const isTimeout =
      error?.code === 'ECONNABORTED' ||
      String(error?.message || '').toLowerCase().includes('timeout');
    const shouldRetryWake =
      config &&
      !config.__retryWake &&
      !config.__skipWakeRetry &&
      (status === 503 || status === 502 || (!error.response && !isTimeout));
    if (shouldRetryWake) {
      config.__retryWake = true;
      await new Promise((r) => setTimeout(r, 4000));
      return axios.request(config);
    }
    return Promise.reject(error);
  }
);

// Staff JWT expired or signed with a different server secret — send baristas back to login
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (typeof window === 'undefined') return Promise.reject(error);
    const path = window.location.pathname || '';
    const onStaffConsole = path === '/barista' || path.startsWith('/barista/');
    const onLogin = path.includes('/barista/login');
    const status = error.response?.status;
    const msg = error.response?.data?.error;
    const hadStaffToken = Boolean(getBaristaTokenSync());
    const sessionEnded =
      msg === 'Invalid or expired token' ||
      msg === 'Staff access required' ||
      msg === 'Barista access required';
    if (onStaffConsole && !onLogin && hadStaffToken && status === 403 && sessionEnded) {
      void setBaristaToken(null);
      void setBaristaProfile(null);
      window.location.assign('/barista/login');
    }
    return Promise.reject(error);
  }
);

// Helper function to make API calls
export const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  await hydrateAuthTokens();
  const url = `${API_BASE_URL}${endpoint}`;
  const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
  const token = getAuthHeaderForPath(path);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};

export default axios;
